/**
 * @file experience.js
 * @description Friendly therapist-experience formatting. Prefers month
 *   precision (from credentialedDate) and falls back to year precision
 *   (credentialedYear) or a legacy experience number. Sub-year shows months;
 *   zero shows "New". Single source of truth for every experience label.
 * @module lib/experience
 */
function monthsSince(dateStr) {
  const m = /^(\d{4})-(\d{2})/.exec(String(dateStr || ""));
  if (!m) return null;
  const y = parseInt(m[1]), mo = parseInt(m[2]);
  if (!y || !mo) return null;
  const now = new Date();
  return Math.max(0, (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - mo));
}

/**
 * Coerce a credentialed-month value to a clean "YYYY-MM", or null if it isn't
 * a usable month. This is the guard between user input and the
 * therapist_profiles.credentialed_date column, which is a real Postgres
 * `date` — anything else makes the profile upsert throw.
 *
 * Why this is needed at all: <input type="month"> is unsupported in Safari,
 * which silently degrades it to a free-text box (no picker, no masking), so
 * arbitrary text reaches the save path on those browsers.
 *
 * Accepts "YYYY-MM" and "YYYY-MM-DD" (trailing day ignored). Rejects
 * out-of-range months, implausible years, and anything non-numeric.
 *
 * @param {string} value
 * @returns {string|null} "YYYY-MM", or null when unusable
 */
export function normalizeCredentialedMonth(value) {
  const m = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(String(value ?? "").trim());
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (month < 1 || month > 12) return null;
  // BACB's first certifications were issued in 2000; allow slack for
  // pre-certification practice while still rejecting typos like "0202".
  const nowYear = new Date().getFullYear();
  if (year < 1960 || year > nowYear) return null;
  return `${m[1]}-${m[2]}`;
}

/**
 * UI-facing soft validation for the credentialed-month field. Returns an
 * empty string when the value is acceptable (including empty — the field is
 * optional), or a human-readable reason when it isn't.
 *
 * @param {string} value - raw input value
 * @param {string} maxMonth - current month as "YYYY-MM"; future dates rejected
 * @param {function} [t] - i18next translator
 * @returns {string} error message, or "" when valid
 */
export function validateCredentialedMonth(value, maxMonth, t) {
  const raw = String(value ?? "").trim();
  if (!raw) return ""; // optional field — empty is fine
  const tr = typeof t === "function" ? t : (_k, o) => (o && o.defaultValue) || "";
  const normalized = normalizeCredentialedMonth(raw);
  if (!normalized) {
    return tr("onboarding.credentialedDateInvalid", {
      defaultValue: "Enter a month and year, like 2022-07.",
    });
  }
  if (maxMonth && normalized > maxMonth) {
    return tr("onboarding.credentialedDateFuture", {
      defaultValue: "This can't be in the future.",
    });
  }
  return "";
}

/** Total months of experience, or null if unknown. Year-only sources return whole years × 12. */
export function experienceMonths({ credentialedDate, credentialedYear } = {}) {
  if (credentialedDate) {
    const t = monthsSince(credentialedDate);
    if (t != null) return t;
  }
  if (credentialedYear) {
    return Math.max(0, new Date().getFullYear() - parseInt(credentialedYear)) * 12;
  }
  return null;
}

/**
 * Friendly label: "1 yr 3 mo" | "8 mo" | "5 yr" | "New" | null.
 * Month parts only show when a real month was captured (credentialedDate);
 * year-only sources never fabricate months.
 * @param {object} p - { credentialedDate?, credentialedYear?, experience? }
 * @param {function} [t] - i18next translator
 */
export function formatExperience(p = {}, t) {
  const tr = typeof t === "function" ? t : (_k, o) => (o && o.defaultValue) || "";
  const hasMonth = !!p.credentialedDate;
  const total = experienceMonths(p);
  if (total == null) {
    const legacy = parseInt(p.experience);
    return (Number.isFinite(legacy) && legacy > 0)
      ? tr("experience.yr", { count: legacy, defaultValue: `${legacy} yr` })
      : null;
  }
  if (total < 1) return tr("experience.new", { defaultValue: "New" });
  const years = Math.floor(total / 12);
  const months = total % 12;
  if (!hasMonth) {
    return years > 0
      ? tr("experience.yr", { count: years, defaultValue: `${years} yr` })
      : tr("experience.new", { defaultValue: "New" });
  }
  if (years === 0) return tr("experience.mo", { count: months, defaultValue: `${months} mo` });
  if (months === 0) return tr("experience.yr", { count: years, defaultValue: `${years} yr` });
  return tr("experience.yr", { count: years, defaultValue: `${years} yr` }) + " " +
         tr("experience.mo", { count: months, defaultValue: `${months} mo` });
}
